'use client'

import { useState } from 'react'

import { track } from '@amplitude/analytics-browser'

import { browserNavigate } from '@/lib/browser-navigation'
import type { SearchWidgetCopy } from '@/lib/search-widget/copy'
import { cn } from '@/lib/utils'
import { WorldCupMascot } from '@/components/c2c/world-cup-mascot'
import { type DisplayDateAnchor } from '@/i18n/dates'
import { useLocale } from '@/i18n/locale'

import { LocationCombobox } from './location-combobox'
import { PassengerSelect } from './passenger-select'
import { SearchDatePicker } from './search-date-picker'
import { useSearchCarrier, useSearchSelection } from './search-selection-context'
import { useSearchSubmit } from './use-search-submit'
import { useSearchTracking } from './use-search-tracking'
import { useWidgetState, type WidgetStateTuple } from './use-widget-state'

export type { WidgetStateTuple }

interface SearchWidgetBaseProps {
    initialFrom?: string
    initialFromId?: string
    initialTo?: string
    initialToId?: string
    initialMode?: string
    initialDate?: string
    compact?: boolean
    /** Unique suffix for radio button names to avoid conflicts with multiple instances */
    formId?: string
    staticBaseUrl?: string
    /** World Cup routes swap the peek-over Chiku for the soccer-Chiku variant. */
    isWorldCup?: boolean
    /** Always render the mobile layout regardless of viewport width. */
    forceMobile?: boolean
}

/**
 * `dateAnchor` is **required when the widget owns its state** (uncontrolled): the
 * server-computed anchor is what keeps the depart-date SSR-safe, and a dropped anchor
 * is the React #418 regression (invariants.md#ssr-safe-rendered-dates). It's optional
 * in **controlled** mode (`widgetState` provided), where the internal state — and thus
 * the anchor — is unused; the parent that owns the state supplies the anchor instead.
 */
export type SearchWidgetProps = SearchWidgetBaseProps &
    (
        | { widgetState: WidgetStateTuple; dateAnchor?: DisplayDateAnchor }
        | { widgetState?: undefined; dateAnchor: DisplayDateAnchor }
    )

interface ValidationErrors {
    from?: boolean
    to?: boolean
    departDate?: boolean
}

/** The widget's interactive half, which the server-side `SearchWidget` hands its copy. */
export function SearchWidgetClient({
    copy,
    initialFrom,
    initialFromId,
    initialTo,
    initialToId,
    initialMode = 'bus',
    initialDate,
    dateAnchor,
    compact = false,
    widgetState,
    formId,
    staticBaseUrl,
    isWorldCup = false,
    forceMobile = false,
}: SearchWidgetProps & { readonly copy: SearchWidgetCopy }) {
    // A `widgetState` prop wins over the surface-owned selection, which wins over a
    // selection of this widget's own. Only the owner restores and persists —
    // see `UseWidgetStateOptions.enabled`.
    const surfaceSelection = useSearchSelection()
    const suppliedSelection = widgetState ?? surfaceSelection
    const ownSelection = useWidgetState(
        {
            from: initialFrom,
            fromId: initialFromId,
            to: initialTo,
            toId: initialToId,
            mode: initialMode as 'bus' | 'train' | 'ferry' | 'flight',
            // An unsupplied initialDate must stay `undefined` — a '' fallback is a
            // *defined* value that survives the undefined-only filter and clobbers
            // the restored stored date (#795).
            departDate: initialDate,
        },
        dateAnchor,
        { enabled: !suppliedSelection },
    )
    const [state, setState] = suppliedSelection ?? ownSelection
    const radioName = formId ? `tripType-${formId}` : 'tripType'

    const [errors, setErrors] = useState<ValidationErrors>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const tracking = useSearchTracking()
    const locale = useLocale()
    const carrier = useSearchCarrier()
    const { submit } = useSearchSubmit(state, locale, carrier)
    const [swapRotation, setSwapRotation] = useState(0)

    const trackFormInteraction = (interactionType: string, extra?: Record<string, string>) => {
        track('Search Form Interacted', {
            interaction_type: interactionType,
            ...extra,
        })
    }

    const handleSwap = () => {
        setSwapRotation((r) => r - 180)
        trackFormInteraction('origin_destination_swapped')
        setState((s) => ({
            ...s,
            from: s.to,
            fromId: s.toId,
            to: s.from,
            toId: s.fromId,
        }))
        setErrors((e) => ({ ...e, from: false, to: false }))
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        const result = submit()
        if (result.kind === 'invalid') {
            setErrors(result.errors)
            return
        }
        setErrors({})
        setIsSubmitting(true)
        browserNavigate(result.url)
    }

    const isOneWay = state.tripType === 'one-way'

    return (
        <form
            onSubmit={handleSubmit}
            className={cn('rounded-lg', compact ? 'flex items-end gap-2' : '')}
            data-testid="search-widget"
            data-section="search"
        >
            {!compact && (
                <div className="mb-4 flex gap-6">
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-[#2e3033]">
                        <span
                            className={cn(
                                'flex h-5 w-5 items-center justify-center rounded-full border-2',
                                isOneWay ? 'border-[#188da1]' : 'border-[#b0b5bc]',
                            )}
                        >
                            {isOneWay && <span className="h-2.5 w-2.5 rounded-full bg-[#188da1]" />}
                        </span>
                        <input
                            type="radio"
                            name={radioName}
                            value="one-way"
                            checked={isOneWay}
                            onChange={() => {
                                setState((s) => ({ ...s, tripType: 'one-way' }))
                                trackFormInteraction('trip_type_changed', {
                                    updated_from: 'round_trip',
                                    updated_to: 'one_way',
                                })
                            }}
                            className="sr-only"
                        />
                        {copy.tripTypeOneWay.text}
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-[#2e3033]">
                        <span
                            className={cn(
                                'flex h-5 w-5 items-center justify-center rounded-full border-2',
                                !isOneWay ? 'border-[#188da1]' : 'border-[#b0b5bc]',
                            )}
                        >
                            {!isOneWay && (
                                <span className="h-2.5 w-2.5 rounded-full bg-[#188da1]" />
                            )}
                        </span>
                        <input
                            type="radio"
                            name={radioName}
                            value="round-trip"
                            checked={!isOneWay}
                            onChange={() => {
                                setState((s) => ({ ...s, tripType: 'round-trip' }))
                                trackFormInteraction('trip_type_changed', {
                                    updated_from: 'one_way',
                                    updated_to: 'round_trip',
                                })
                            }}
                            className="sr-only"
                        />
                        {copy.tripTypeRoundTrip.text}
                    </label>
                </div>
            )}

            <div
                className={cn(
                    'relative',
                    compact
                        ? 'flex flex-1 items-end gap-2'
                        : cn(
                              'grid gap-2',
                              // Both trip types give the locations a row of their own at
                              // md (the trip row spans all three columns and splits
                              // itself); one-way collapses back to a single six-column
                              // row from lg up. Sharing one row is what clipped the
                              // ", State" suffix off a long place value (#632) — the
                              // dates section holds two pickers in round-trip, so no
                              // split of a single row leaves the From/To inputs wide
                              // enough.
                              !forceMobile &&
                                  (isOneWay
                                      ? 'md:grid-cols-[1fr_auto_1fr] lg:grid-cols-[1fr_auto_1fr_1fr_auto_auto]'
                                      : 'md:grid-cols-[1fr_1fr_auto]'),
                          ),
                )}
            >
                {/* Chiku mascot — md breakpoint only (lg+ uses the desktop variant in the
                    header). World Cup routes show the full-body soccer-Chiku variant
                    (animated, playing with the ball), sized larger. */}
                {staticBaseUrl &&
                    (isWorldCup ? (
                        <WorldCupMascot
                            staticBaseUrl={staticBaseUrl}
                            className={cn(
                                'absolute top-[-128px] right-[6px] z-10 hidden',
                                !forceMobile && 'md:block md:h-[120px] md:w-[155px] lg:hidden',
                            )}
                        />
                    ) : (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            alt=""
                            className={cn(
                                // Sits clear above the 66px-tall glyph's own box, in the
                                // empty space right of the trip-type choice — a smaller
                                // offset drops it onto the first field row.
                                'absolute top-[-78px] right-[10px] z-10 hidden',
                                !forceMobile && 'md:block lg:hidden',
                            )}
                            height={66}
                            // Lazy is what keeps a phone, where the image is `hidden`,
                            // from fetching it: React preloads an eager `<img>` in the
                            // document head whatever its CSS says.
                            loading="lazy"
                            src="/next-static/images/chiku-tablet.svg"
                            width={82}
                        />
                    ))}

                {/* All fields grouped in one card on mobile, inline grid items on desktop */}
                <div
                    className={cn(
                        'relative overflow-hidden rounded-[7px] border border-[#d9dee5]',
                        !forceMobile && 'md:contents',
                    )}
                >
                    {/* Trip row — From/swap/To. `contents` leaves the mobile card
                        (and its dividers) untouched; round-trip promotes this to a
                        real grid item spanning the full desktop row, with its own
                        1fr/swap/1fr split inside, so the locations get a whole row
                        instead of competing with the dates for one (#632). */}
                    <div
                        className={cn(
                            'contents',
                            // Splitting the row here rather than leaning on the outer
                            // grid is what keeps the swap snug: the outer middle track is
                            // `auto`, shared with the seat-count field below, so a trip
                            // row laid out against it inherits that field's width and
                            // strands the swap in the middle of it.
                            !forceMobile &&
                                'md:col-span-3 md:grid md:grid-cols-[1fr_auto_1fr] md:gap-2',
                            // One-way rejoins the outer grid at lg, where all six fields
                            // share a single row.
                            !forceMobile && isOneWay && 'lg:contents',
                        )}
                    >
                        <LocationCombobox
                            closeSuggestions={copy.closeSuggestions}
                            label={copy.from.text}
                            value={state.from}
                            onChange={(value, placeId) => {
                                const previous = state.from
                                setState((s) => ({ ...s, from: value, fromId: placeId }))
                                setErrors((e) => ({ ...e, from: false }))
                                if (placeId) {
                                    tracking.trackPredictionSelected('from', value, placeId)
                                    trackFormInteraction('origin_selected', {
                                        ...(previous && { updated_from: previous }),
                                        updated_to: value,
                                    })
                                }
                            }}
                            placeholder={copy.locationPlaceholder.text}
                            hasError={errors.from}
                            grouped
                            forceMobile={forceMobile}
                        />

                        {/* Swap — overlaid circle on mobile, inline rounded-rect on desktop */}
                        <div
                            className={cn(
                                'absolute top-12 right-4 z-10 -translate-y-1/2',
                                !forceMobile &&
                                    'md:static md:flex md:translate-y-0 md:items-center md:justify-center',
                            )}
                        >
                            <button
                                type="button"
                                onClick={handleSwap}
                                aria-label={copy.swapLocations.text}
                                className={cn(
                                    'flex h-12 w-12 rotate-90 items-center justify-center rounded-full border border-[#d9dee5] bg-white text-[#8c939c] transition-colors hover:bg-[#f5f6f7]',
                                    !forceMobile && 'md:rotate-0 md:rounded-[7px]',
                                )}
                            >
                                <svg
                                    className="h-6 w-6 transition-transform duration-300 ease-in-out"
                                    style={{ transform: `rotate(${swapRotation}deg)` }}
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path d="M23.7071 8.70714C24.0976 8.31661 24.0976 7.68345 23.7071 7.29292L19.7071 3.29292C19.4211 3.00692 18.991 2.92137 18.6173 3.07615C18.2436 3.23093 18 3.59557 18 4.00003L18 6.00003L11 6.00003C10.4477 6.00003 10 6.44775 10 7.00003L10 9.00003C10 9.55232 10.4477 10 11 10L18 10L18 12C18 12.4045 18.2436 12.7691 18.6173 12.9239C18.991 13.0787 19.4211 12.9931 19.7071 12.7071L23.7071 8.70714Z" />
                                    <path d="M14 15C14 14.4477 13.5523 14 13 14L6 14L6 12C6 11.5956 5.75636 11.2309 5.38268 11.0761C5.00901 10.9214 4.57889 11.0069 4.29289 11.2929L0.292892 15.2929C-0.097632 15.6834 -0.097632 16.3166 0.292892 16.7071L4.29289 20.7071C4.57889 20.9931 5.00901 21.0787 5.38268 20.9239C5.75636 20.7691 6 20.4045 6 20L6 18L13 18C13.5523 18 14 17.5523 14 17L14 15Z" />
                                </svg>
                            </button>
                        </div>

                        <div
                            className={cn('border-t border-[#d9dee5]', !forceMobile && 'md:hidden')}
                        />

                        <LocationCombobox
                            closeSuggestions={copy.closeSuggestions}
                            label={copy.to.text}
                            value={state.to}
                            onChange={(value, placeId) => {
                                const previous = state.to
                                setState((s) => ({ ...s, to: value, toId: placeId }))
                                setErrors((e) => ({ ...e, to: false }))
                                if (placeId) {
                                    tracking.trackPredictionSelected('to', value, placeId)
                                    trackFormInteraction('destination_selected', {
                                        ...(previous && { updated_from: previous }),
                                        updated_to: value,
                                    })
                                }
                            }}
                            placeholder={copy.locationPlaceholder.text}
                            hasError={errors.to}
                            grouped
                            forceMobile={forceMobile}
                        />
                    </div>

                    <div className={cn('border-t border-[#d9dee5]', !forceMobile && 'md:hidden')} />

                    {/* Date section — inside mobile card, own border on desktop */}
                    {isOneWay ? (
                        <div
                            className={cn(
                                !forceMobile && 'md:overflow-hidden md:rounded-[7px] md:border',
                                !forceMobile &&
                                    (errors.departDate
                                        ? 'md:border-red-500'
                                        : 'md:border-[#d9dee5]'),
                            )}
                        >
                            <SearchDatePicker
                                today={copy.today}
                                label={copy.depart.text}
                                value={state.departDate}
                                hasError={errors.departDate}
                                onChange={(date) => {
                                    const previous = state.departDate
                                    setState((s) => ({ ...s, departDate: date }))
                                    setErrors((e) => ({ ...e, departDate: false }))
                                    tracking.trackDateSelected(date)
                                    trackFormInteraction('date_selected', {
                                        ...(previous && { updated_from: previous }),
                                        updated_to: date,
                                    })
                                }}
                                grouped
                            />
                        </div>
                    ) : (
                        <div
                            className={cn(
                                'flex flex-row items-center',
                                !forceMobile && 'md:overflow-hidden md:rounded-[7px] md:border',
                                !forceMobile &&
                                    (errors.departDate
                                        ? 'md:border-red-500'
                                        : 'md:border-[#d9dee5]'),
                            )}
                        >
                            <div className="flex-1">
                                <SearchDatePicker
                                    today={copy.today}
                                    label={copy.depart.text}
                                    value={state.departDate}
                                    hasError={errors.departDate}
                                    onChange={(date) => {
                                        const previous = state.departDate
                                        setState((s) => ({ ...s, departDate: date }))
                                        setErrors((e) => ({ ...e, departDate: false }))
                                        tracking.trackDateSelected(date)
                                        trackFormInteraction('date_selected', {
                                            ...(previous && { updated_from: previous }),
                                            updated_to: date,
                                        })
                                    }}
                                    grouped
                                />
                            </div>
                            <div className="h-8 w-px shrink-0 bg-[#d5dde6]" />
                            <div className="flex-1">
                                <SearchDatePicker
                                    today={copy.today}
                                    label={copy.return.text}
                                    value={state.returnDate}
                                    onChange={(date) => {
                                        const previous = state.returnDate
                                        setState((s) => ({ ...s, returnDate: date }))
                                        tracking.trackDateSelected(date)
                                        trackFormInteraction('date_selected', {
                                            ...(previous && { updated_from: previous }),
                                            updated_to: date,
                                        })
                                    }}
                                    placeholder={copy.addReturn.text}
                                    minDate={state.departDate}
                                    onTodayClick={() => {
                                        const previous = state.returnDate
                                        const d = new Date()
                                        const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                                        setState((s) => ({
                                            ...s,
                                            departDate: today,
                                            returnDate: today,
                                        }))
                                        tracking.trackDateSelected(today)
                                        trackFormInteraction('date_selected', {
                                            ...(previous && { updated_from: previous }),
                                            updated_to: today,
                                        })
                                    }}
                                    grouped
                                />
                            </div>
                        </div>
                    )}

                    <div className={cn('border-t border-[#d9dee5]', !forceMobile && 'md:hidden')} />

                    <PassengerSelect
                        passengerCount={copy.passengerCount}
                        passengers={copy.passengers}
                        value={state.passengers}
                        onChange={(passengers) => {
                            const previous = state.passengers
                            setState((s) => ({ ...s, passengers }))
                            trackFormInteraction('passenger_count_changed', {
                                updated_from: String(previous),
                                updated_to: String(passengers),
                            })
                        }}
                        grouped
                        forceMobile={forceMobile}
                    />
                </div>

                <div className={cn('mt-3 flex items-end', !forceMobile && 'md:mt-0')}>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        aria-busy={isSubmitting}
                        className={cn(
                            'relative flex h-12 w-full items-center justify-center rounded-[7px] bg-[#d13b3b] px-8 text-sm font-bold tracking-[2px] text-white uppercase transition-colors hover:bg-[#b83232] disabled:cursor-not-allowed disabled:opacity-70',
                            // Shrinks to its content only from lg up, where the button
                            // lands in an `auto` track. At md a one-way widget wraps onto
                            // two rows and the button sits in a `1fr` column beneath the
                            // destination field, so an auto width strands it mid-row and
                            // leaves the column's remainder empty.
                            !forceMobile && 'lg:w-auto',
                        )}
                    >
                        {/* Keep the label in flow (reserving the button's width) but
                            transparent while submitting, so swapping in the loading
                            dots doesn't shrink the lg:w-auto button. opacity-0 (not
                            invisible) preserves the button's accessible name. */}
                        <span className={cn(isSubmitting && 'opacity-0')}>{copy.search.text}</span>
                        {isSubmitting && (
                            <span
                                aria-hidden="true"
                                className="absolute inset-0 flex items-center justify-center gap-1.5"
                            >
                                <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
                                <span className="h-2 w-2 animate-pulse rounded-full bg-white [animation-delay:233ms]" />
                                <span className="h-2 w-2 animate-pulse rounded-full bg-white [animation-delay:466ms]" />
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </form>
    )
}
